import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * BulkUploadDialog — admin-only template download + dry-run preview + commit.
 *
 * Props:
 *   - section: one of leaves|timesheet|attendance|purchase_requests|assets|clients|vendors
 *   - label:   user-facing section label (e.g. "Leaves")
 *   - onCommitted: callback fired after a successful commit (parent should reload)
 */
export default function BulkUploadDialog({ section, label, onCommitted }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [commitReport, setCommitReport] = useState(null);   // Phase 83 — keep post-commit report visible
  const inputRef = useRef(null);

  if (user?.role !== "admin" && user?.role !== "founder") return null;

  const reset = () => {
    setFile(null);
    setPreview(null);
    setCommitReport(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const { data, headers } = await api.get(`/bulk/${section}/template`, { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${section}-template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Could not download template: ${e?.response?.status || e?.message || e}`);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/bulk/${section}/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err));
      reset();
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/bulk/${section}/commit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const s = data?.summary || {};
      // Phase 83 — keep dialog open with a confirmation report so the user
      // can see EXACTLY which rows failed. `data.rows` mirrors the preview
      // shape but reflects the actual insert/update/error status.
      setCommitReport({
        inserted: s.inserted || 0,
        updated: s.updated || 0,
        failed: s.failed || 0,
        rows: data?.rows || [],
      });
      // The parent still gets a callback so its own view can refresh.
      if (onCommitted) onCommitted(data);
      if ((s.failed || 0) === 0) {
        toast.success(`Imported ${s.inserted} new · ${s.updated} updated`);
      } else {
        toast.warning(`Imported ${s.inserted} new · ${s.updated} updated · ${s.failed} FAILED — see report`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  const headerErrors = preview?.header_errors || [];
  const rows = preview?.rows || [];
  const summary = preview?.summary || { total: 0, would_insert: 0, would_update: 0, failed: 0 };
  const canCommit = !!preview && headerErrors.length === 0 && (summary.would_insert + summary.would_update) > 0 && !committing;

  return (
    <>
      <div className="inline-flex items-center gap-1.5" data-testid={`bulk-${section}-actions`}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={downloadTemplate}
          className="rounded-sm h-8 text-xs"
          data-testid={`bulk-${section}-download-template`}
        >
          <Download size={12} className="mr-1.5" />
          Download template
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => { setOpen(true); reset(); }}
          className="rounded-sm h-8 text-xs"
          data-testid={`bulk-${section}-open-upload`}
        >
          <Upload size={12} className="mr-1.5" />
          Bulk upload
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="rounded-sm max-w-4xl max-h-[88vh] overflow-y-auto" data-testid={`bulk-${section}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileSpreadsheet size={18} className="text-[#F54800]" />
              Bulk upload · {label || section}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Backfill historical {label?.toLowerCase() || section} from a filled .xlsx or .csv. The columns must match the downloaded template. Existing records (by dedupe key) are updated; new ones are inserted. Every change is audit-logged.
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: file picker */}
          <div className="card-flat p-4 mt-2">
            <div className="text-xs font-medium text-slate-700 mb-2">1. Choose the filled template (.xlsx or .csv)</div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv,text/csv"
              onChange={onFile}
              className="block text-xs"
              data-testid={`bulk-${section}-file-input`}
            />
            {file && (
              <div className="mt-2 text-[11px] text-slate-500">
                {file.name} · {(file.size / 1024).toFixed(1)} KB
                {previewing && <span className="ml-2 inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> validating…</span>}
              </div>
            )}
          </div>

          {/* Step 2: header errors */}
          {headerErrors.length > 0 && (
            <div className="card-flat p-4 border-l-4 border-l-red-500 bg-red-50/30">
              <div className="text-xs font-medium text-red-700 mb-1.5 inline-flex items-center gap-1.5">
                <AlertTriangle size={12} /> Header errors — fix and re-upload
              </div>
              <ul className="text-[11px] text-red-700 list-disc pl-4 space-y-0.5">
                {headerErrors.map((e, i) => <li key={`header-err-${i}-${e}`}>{e}</li>)}
              </ul>
            </div>
          )}

          {/* Step 3: row preview */}
          {preview && headerErrors.length === 0 && (
            <div className="card-flat overflow-hidden mt-1" data-testid={`bulk-${section}-preview`}>
              <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-3 text-xs">
                <div className="font-medium text-slate-700">2. Preview · {summary.total} rows</div>
                <span className="text-green-700" data-testid={`bulk-${section}-would-insert`}>
                  <CheckCircle2 size={11} className="inline -mt-px mr-0.5" /> {summary.would_insert} new
                </span>
                <span className="text-blue-700" data-testid={`bulk-${section}-would-update`}>
                  ↻ {summary.would_update} update
                </span>
                <span className="text-red-700" data-testid={`bulk-${section}-failed`}>
                  <X size={11} className="inline -mt-px mr-0.5" /> {summary.failed} skip
                </span>
                {summary.with_warnings > 0 && (
                  <span className="text-amber-700" data-testid={`bulk-${section}-with-warnings`}>
                    <AlertTriangle size={11} className="inline -mt-px mr-0.5" /> {summary.with_warnings} ambiguous date{summary.with_warnings === 1 ? "" : "s"} — verify
                  </span>
                )}
              </div>
              <div className="max-h-[40vh] overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 w-12">#</th>
                      <th className="text-left px-3 py-2 w-20">Action</th>
                      <th className="text-left px-3 py-2">Data / error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-4 text-center text-slate-400">No data rows found.</td></tr>
                    )}
                    {rows.map((r) => (
                      <tr key={r.row} className={`border-t border-slate-100 ${r.warnings?.length ? "bg-amber-50/50" : ""}`} data-testid={`bulk-${section}-row-${r.row}`}>
                        <td className="px-3 py-2 font-mono text-slate-500">{r.row}</td>
                        <td className="px-3 py-2">
                          {r.action === "insert" && <span className="text-green-700">insert</span>}
                          {r.action === "update" && <span className="text-blue-700">update</span>}
                          {r.action === "error" && <span className="text-red-700">error</span>}
                        </td>
                        <td className="px-3 py-2">
                          {r.action === "error" ? (
                            <ul className="text-red-700 list-disc pl-4 space-y-0.5">
                              {(r.errors || []).map((e, i) => <li key={`row-${r.row}-err-${i}`}>{e}</li>)}
                            </ul>
                          ) : (
                            <div className="text-slate-600 font-mono truncate" title={JSON.stringify(r.data)}>
                              {Object.entries(r.data || {}).filter(([_, v]) => v !== null && v !== "").slice(0, 4).map(([k, v]) => `${k}=${v}`).join("  ·  ")}
                            </div>
                          )}
                          {r.warnings?.length > 0 && (
                            <ul className="text-amber-700 list-none mt-1 space-y-0.5" data-testid={`bulk-${section}-row-${r.row}-warnings`}>
                              {r.warnings.map((w, i) => (
                                <li key={`row-${r.row}-warn-${i}`} className="inline-flex items-start gap-1">
                                  <AlertTriangle size={10} className="shrink-0 mt-0.5" /> <span>{w}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 4 (Phase 83): commit report — sticks around so the user can see
              exactly what got imported and any rows that failed. */}
          {commitReport && (
            <div className="card-flat overflow-hidden mt-3" data-testid={`bulk-${section}-commit-report`}>
              <div className={`px-4 py-2.5 border-b border-slate-200 flex items-center gap-3 text-xs ${
                commitReport.failed === 0 ? "bg-emerald-50/40" : "bg-amber-50/40"
              }`}>
                <div className="font-medium text-slate-800 inline-flex items-center gap-1">
                  {commitReport.failed === 0
                    ? <CheckCircle2 size={13} className="text-emerald-600" />
                    : <AlertTriangle size={13} className="text-amber-600" />}
                  Import complete
                </div>
                <span className="text-emerald-700" data-testid={`bulk-${section}-inserted`}>
                  {commitReport.inserted} new
                </span>
                <span className="text-blue-700" data-testid={`bulk-${section}-updated`}>
                  ↻ {commitReport.updated} updated
                </span>
                <span className={commitReport.failed === 0 ? "text-slate-500" : "text-red-700"} data-testid={`bulk-${section}-failed-count`}>
                  <X size={11} className="inline -mt-px mr-0.5" /> {commitReport.failed} failed
                </span>
              </div>
              {commitReport.failed > 0 && (
                <div className="max-h-[30vh] overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 w-12">#</th>
                        <th className="text-left px-3 py-2">Reason it failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commitReport.rows.filter((r) => r.action === "error").map((r) => (
                        <tr key={`cr-${r.row}`} className="border-t border-slate-100" data-testid={`bulk-${section}-failed-row-${r.row}`}>
                          <td className="px-3 py-2 font-mono text-slate-500">{r.row}</td>
                          <td className="px-3 py-2">
                            <ul className="text-red-700 list-disc pl-4 space-y-0.5">
                              {(r.errors || []).map((e, i) => <li key={`cr-${r.row}-${i}`}>{e}</li>)}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setOpen(false); reset(); }} data-testid={`bulk-${section}-close`}>
              {commitReport ? "Close" : "Cancel"}
            </Button>
            {!commitReport && (
              <Button
                type="button"
                onClick={commit}
                disabled={!canCommit}
                className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:opacity-40"
                data-testid={`bulk-${section}-commit`}
              >
                {committing ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Importing…</> : `Import ${summary.would_insert + summary.would_update} rows`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
